import {
    Button,
    Card,
    CardContent,
    IconButton,
    MenuItem,
    Menu,
    ListItemIcon,
    Tooltip,
    Divider,
    Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import React, { useState } from 'react';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import CancelOutlinedIcon from '@material-ui/icons/CancelOutlined';
import ScheduleIcon from '@material-ui/icons/Schedule';
import { daysSince, toHumanReadableDate, toHumanReadableDateTime } from 'helpers/date';
import PictureAsPdfOutlinedIcon from '@material-ui/icons/PictureAsPdfOutlined';
import StoreIcon from '@material-ui/icons/Store';
import PersonOutlineIcon from '@material-ui/icons/PersonOutline';
import RoomIcon from '@material-ui/icons/Room';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import ReplayIcon from '@material-ui/icons/Replay';
import { MERCHANT_TYPE, MERCHANT_TYPE_LABEL } from 'common/merchant';
import { formatAmount } from 'common/money';
import { BILL_STATE, EXTRA_OPTIONS } from 'common/billing';
import Dialog from 'components/Dialog/Dialog';
import style from './style';
import { exportPdf } from './api';

const useStyles = makeStyles(style);

export const StateCard = ({
    bill,
    startAt,
    endAt,
    paymentMethod,
    amount,
    weeks,
    extraPrices,
    extras,
    meters,
    unitPrice,
    onConfirm,
    onPreview,
    onCancelPayment,
    onCancelBill,
    confirmDisabled = false
}) => {
    const classes = useStyles();
    const [pendingAction, setPendingAction] = useState(null); // 'confirm' | 'cancelPayment' | 'cancelBill'

    const getStatusInfo = () => {
        if (bill.state === BILL_STATE.canceled) {
            return {
                bannerClass: classes.statusBannerCancelled,
                iconClass: classes.statusIconCancelled,
                titleClass: classes.statusTitleCancelled,
                subClass: classes.statusSubCancelled,
                Icon: CancelOutlinedIcon,
                title: 'Facture annulée',
                sub: bill.canceled_at
                    ? `Annulée le ${toHumanReadableDate(bill.canceled_at.date)}`
                    : null
            };
        }
        if (bill.state === BILL_STATE.paid) {
            return {
                bannerClass: classes.statusBannerPaid,
                iconClass: classes.statusIconPaid,
                titleClass: classes.statusTitlePaid,
                subClass: classes.statusSubPaid,
                Icon: CheckCircleOutlineIcon,
                title: 'Facture réglée',
                sub: bill.paid_at ? `Réglée le ${toHumanReadableDate(bill.paid_at.date)}` : null
            };
        }

        const days = daysSince(bill.created_at.date);

        return {
            bannerClass: classes.statusBannerUnpaid,
            iconClass: classes.statusIconUnpaid,
            titleClass: classes.statusTitleUnpaid,
            subClass: classes.statusSubUnpaid,
            Icon: ScheduleIcon,
            title: 'Non réglée',
            sub: days !== null ? `En attente depuis ${days} jour${days > 1 ? 's' : ''}` : null
        };
    };

    const status = getStatusInfo();
    const StatusIcon = status.Icon;

    /* Anchor pour le menu "Plus d'actions" */
    const [actionsMenuAnchor, setActionsMenuAnchor] = useState(null);
    const activeExtras = EXTRA_OPTIONS.filter(opt => extras[opt.key]);

    return (
        <Card className={classes.rightPanel}>
            <CardContent style={{ padding: 0 }}>
                <div className={`${classes.statusBanner} ${status.bannerClass}`}>
                    <div className={`${classes.statusIcon} ${status.iconClass}`}>
                        <StatusIcon style={{ fontSize: 18 }} />
                    </div>
                    <div>
                        <Typography className={`${classes.statusTitle} ${status.titleClass}`}>
                            {status.title}
                        </Typography>
                        {status.sub ? (
                            <Typography className={`${classes.statusSub} ${status.subClass}`}>
                                {status.sub}
                            </Typography>
                        ) : null}
                    </div>
                </div>

                <div className={classes.panelIdHeader}>
                    <div className={classes.panelIdLeft}>
                        <Typography component="span" className={classes.panelIdNumber}>
                            {bill.number}
                        </Typography>
                        {bill.created_at ? (
                            <Typography component="span" className={classes.panelIdDate}>
                                émise le {toHumanReadableDate(bill.created_at.date)}
                            </Typography>
                        ) : null}
                    </div>
                    <Tooltip title="Ouvrir le PDF dans un nouvel onglet">
                        <Button
                            variant="outlined"
                            size="small"
                            className={classes.previewBtn}
                            onClick={() => exportPdf(bill.number)}
                            startIcon={<PictureAsPdfOutlinedIcon />}>
                            Aperçu
                        </Button>
                    </Tooltip>
                </div>

                <div className={classes.panelInfo}>
                    <div className={classes.panelInfoRow}>
                        <StoreIcon className={classes.panelInfoIcon} />
                        <div className={classes.panelInfoContent}>
                            <Typography className={classes.panelInfoKey}>Marché</Typography>
                            <Typography className={classes.panelInfoValue}>
                                {bill.market}
                            </Typography>
                        </div>
                    </div>

                    <div className={classes.panelInfoRow}>
                        <PersonOutlineIcon className={classes.panelInfoIcon} />
                        <div className={classes.panelInfoContent}>
                            <Typography className={classes.panelInfoKey}>Raison sociale</Typography>
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center'
                                }}>
                                <Typography className={classes.panelInfoValue}>
                                    {bill.merchant}
                                </Typography>
                                <Typography variant="caption" className={classes.merchantTypeChip}>
                                    {MERCHANT_TYPE_LABEL[bill.type]}
                                </Typography>
                            </div>
                        </div>
                    </div>

                    <div className={classes.panelInfoRow}>
                        <RoomIcon className={classes.panelInfoIcon} />
                        <div className={classes.panelInfoContent}>
                            <Typography className={classes.panelInfoKey}>
                                Numéro de place
                            </Typography>
                            <Typography className={classes.panelInfoValue}>
                                N°{bill.place}
                            </Typography>
                        </div>
                    </div>
                </div>

                {bill.type === MERCHANT_TYPE.subscriber ? (
                    <div className={classes.panelOptions}>
                        <Typography className={classes.sectionLabel} style={{ marginBottom: 8 }}>
                            Période facturée
                        </Typography>
                        <Typography variant="body2">
                            Du{' '}
                            <strong>
                                {startAt === '' ? 'non défini' : toHumanReadableDate(startAt)}
                            </strong>{' '}
                            au{' '}
                            <strong>
                                {endAt === '' ? 'non défini' : toHumanReadableDate(endAt)}
                            </strong>
                        </Typography>
                    </div>
                ) : null}

                <div className={classes.panelOptions}>
                    <Typography className={classes.sectionLabel} style={{ marginBottom: 8 }}>
                        Options
                    </Typography>
                    {activeExtras.length > 0 ? (
                        <div className={classes.optionsChips}>
                            {activeExtras.map(opt => {
                                const ChipIcon = opt.Icon;
                                return (
                                    <span key={opt.key} className={classes.optionChip}>
                                        <ChipIcon />
                                        <Typography variant="body2">{opt.label}</Typography>
                                    </span>
                                );
                            })}
                        </div>
                    ) : (
                        <Typography className={classes.optionsEmpty}>
                            Aucune options supplémentaire
                        </Typography>
                    )}
                </div>

                <div className={classes.recapSection}>
                    <Typography className={classes.sectionLabel} style={{ marginBottom: 8 }}>
                        Calcul
                    </Typography>
                    {EXTRA_OPTIONS.map(opt =>
                        extras[opt.key] ? (
                            <div key={opt.key} className={classes.recapRow}>
                                <Typography className={classes.recapKey} variant="caption">
                                    {opt.label}
                                </Typography>
                                <Typography className={classes.recapVal} variant="caption">
                                    {formatAmount(
                                        extraPrices[opt.key] === ''
                                            ? bill[`${opt.key}_price`]
                                            : extraPrices[opt.key]
                                    )}{' '}
                                    €
                                </Typography>
                            </div>
                        ) : null
                    )}
                    <div className={classes.recapRow}>
                        <Typography className={classes.recapKey} variant="caption">
                            Qté * P.U {bill.type === MERCHANT_TYPE.subscriber ? '* Semaine' : ''}
                        </Typography>
                        <Typography className={classes.recapVal} variant="caption">
                            {meters === '' ? bill.meters : meters} m *{' '}
                            {formatAmount(unitPrice === '' ? bill.unit_price : unitPrice)}€
                            {bill.type === MERCHANT_TYPE.subscriber
                                ? ` * ${weeks === '' ? bill.weeks : weeks}`
                                : ''}{' '}
                        </Typography>
                    </div>
                    <div className={classes.recapTotal}>
                        <Typography className={classes.recapTotalKey}>Total</Typography>
                        <Typography className={classes.recapTotalVal}>
                            {formatAmount(amount)} €
                        </Typography>
                    </div>
                </div>

                {/* ── Actions : bouton principal + menu "Plus d'actions" ── */}
                {bill.state !== BILL_STATE.canceled ? (
                    <div className={classes.actionZone}>
                        <Tooltip
                            title={
                                !paymentMethod && bill.state === BILL_STATE.paid
                                    ? "Sélectionnez d'abord un mode de paiement"
                                    : ''
                            }>
                            <Button
                                variant="contained"
                                color="primary"
                                className={classes.btnPrimary}
                                startIcon={<CheckCircleOutlineIcon />}
                                disabled={confirmDisabled}
                                onClick={() => setPendingAction('confirm')}>
                                Valider les modifications
                            </Button>
                        </Tooltip>

                        <Tooltip title="Plus d'actions">
                            <IconButton
                                className={classes.btnMore}
                                size="small"
                                onClick={e => setActionsMenuAnchor(e.currentTarget)}
                                aria-label="Plus d'actions"
                                aria-haspopup="true"
                                aria-expanded={Boolean(actionsMenuAnchor)}>
                                <MoreVertIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>

                        <Menu
                            anchorEl={actionsMenuAnchor}
                            open={Boolean(actionsMenuAnchor)}
                            onClose={() => setActionsMenuAnchor(null)}
                            getContentAnchorEl={null}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right'
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right'
                            }}
                            PaperProps={{
                                style: { minWidth: 230, marginTop: 4 }
                            }}>
                            {bill.state === BILL_STATE.paid && (
                                <MenuItem
                                    className={classes.menuItemWarning}
                                    onClick={() => {
                                        setActionsMenuAnchor(null);
                                        setPendingAction('cancelPayment');
                                    }}>
                                    <ListItemIcon>
                                        <ReplayIcon fontSize="small" />
                                    </ListItemIcon>
                                    Annuler l'encaissement
                                </MenuItem>
                            )}
                            {bill.state === BILL_STATE.paid && <Divider />}
                            <MenuItem
                                className={classes.menuItemDanger}
                                onClick={() => {
                                    setActionsMenuAnchor(null);
                                    setPendingAction('cancelBill');
                                }}>
                                <ListItemIcon>
                                    <CancelOutlinedIcon fontSize="small" />
                                </ListItemIcon>
                                Annuler la facture
                            </MenuItem>
                        </Menu>
                    </div>
                ) : null}
            </CardContent>

            <Dialog
                title={
                    pendingAction === 'confirm'
                        ? 'Confirmer les modifications ⚠️'
                        : pendingAction === 'cancelPayment'
                        ? "Annuler l'encaissement ⚠️"
                        : 'Annuler la facture ⚠️'
                }
                open={Boolean(pendingAction)}
                onClose={() => setPendingAction(null)}
                action={
                    pendingAction === 'confirm' ? (
                        <>
                            <Button
                                variant="contained"
                                color="secondary"
                                onClick={() => {
                                    setPendingAction(null);
                                    onConfirm && onConfirm(true);
                                }}>
                                Valier et envoyer une copie au commerçant
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => {
                                    setPendingAction(null);
                                    onConfirm && onConfirm();
                                }}>
                                Valider
                            </Button>
                        </>
                    ) : pendingAction === 'cancelPayment' ? (
                        <Button
                            variant="contained"
                            color="secondary"
                            startIcon={<ReplayIcon />}
                            onClick={() => {
                                setPendingAction(null);
                                onCancelPayment && onCancelPayment();
                            }}>
                            Confirmer l'annulation
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            color="secondary"
                            startIcon={<CancelOutlinedIcon />}
                            onClick={() => {
                                setPendingAction(null);
                                onCancelBill && onCancelBill();
                            }}>
                            Annuler la facture
                        </Button>
                    )
                }>
                <Typography>
                    {pendingAction === 'confirm'
                        ? 'Cette action est irréversible : la facture actuelle sera annulée et une nouvelle facture sera générée. Confirmez-vous ?'
                        : pendingAction === 'cancelPayment'
                        ? "Cette action est irréversible : l'encaissement sera annulé et la facture repassera en attente de règlement. Confirmez-vous ?"
                        : 'Cette action est irréversible : la facture sera annulée définitivement. Confirmez-vous ?'}
                </Typography>
            </Dialog>
        </Card>
    );
};

export default StateCard;
